import {css, useTheme} from '@mui/material';
import {useState, useMemo, useCallback, useRef} from 'react';
import * as React from 'react';

import {GetStylesFunction, mixins} from '../common';

interface MaximizeWrapperProps {
  children: React.ReactNode;
}

export interface UseMaximizeWrapperReturn {
  maximized: boolean;
  setMaximized: (value: boolean) => void;
  toggleMaximized: () => void;
  MaximizeWrapper: React.FC<MaximizeWrapperProps>;
}

type UseMaximizeWrapperOptions = {
  /** Inline overrides of the inner (white card) box, e.g. a page's own row-gap. */
  innerStyle?: React.CSSProperties;
};

/**
 * Fullscreen toggle for one card of a page (report tables/charts, list results, saved filters): when maximized the
 * wrapper leaves the flow and covers the whole content area (absolute, z-index 100) while keeping the card look
 * inside. Pair with MaximizeButton for the header icon.
 */
export const useMaximizeWrapper = (outerDataName?: string, options?: UseMaximizeWrapperOptions): UseMaximizeWrapperReturn => {
  const [maximized, setMaximized] = useState(false);
  const theme = useTheme();
  // Read through a ref: callers pass the style as an inline literal, and a new object per render must not change
  // the wrapper's identity (a new component type remounts the whole card on every render - an update loop).
  const innerStyleRef = useRef(options?.innerStyle);
  innerStyleRef.current = options?.innerStyle;

  const toggleMaximized = useCallback(() => setMaximized(!maximized), [maximized]);

  const styleParams = useMemo(
    () => ({
      componentMaximized: maximized,
    }),
    [maximized]
  );

  const muiStyles = useMemo(() => getStyles({theme, params: styleParams}), [theme, styleParams]);

  // Only the maximized flag (through muiStyles) and the data-name change the component; both are stable between
  // renders, so the wrapped card keeps its instance (table scroll, focus) across ordinary page re-renders.
  const MaximizeWrapper = useMemo(() => {
    const Component: React.FC<MaximizeWrapperProps> = ({children}) => (
      <div css={muiStyles.componentContentBoxOuter} data-name={outerDataName} data-maximized={maximized}>
        <div css={muiStyles.componentContentBoxInner} style={innerStyleRef.current}>
          {children}
        </div>
      </div>
    );

    Component.displayName = 'MaximizeWrapper';
    return React.memo(Component);
  }, [muiStyles.componentContentBoxInner, muiStyles.componentContentBoxOuter, outerDataName, maximized]);

  return {
    maximized,
    setMaximized,
    toggleMaximized,
    MaximizeWrapper,
  };
};

// Styles
const getStyles: GetStylesFunction = (options) => ({
  componentContentBoxOuter: css`
    ${mixins.contentBox};
    border-radius: ${options?.params?.componentMaximized ? 0 : options?.theme?.borderRadius};
    background: ${options?.params?.componentMaximized ? 'rgb(232, 234, 238)' : '#ffffff'};
    padding: ${options?.params?.componentMaximized ? '16px' : 0};
    padding-left: 0;
    overflow-y: auto;
    position: ${options?.params?.componentMaximized ? 'absolute' : 'static'};
    top: ${options?.params?.componentMaximized ? 0 : 'auto'};
    left: ${options?.params?.componentMaximized ? 0 : 'auto'};
    width: 100%;
    height: 100%;
    z-index: ${options?.params?.componentMaximized ? '100' : 'auto'};
  `,
  componentContentBoxInner: css`
    ${mixins.contentBox};
    border-radius: ${options?.params?.componentMaximized ? options?.theme?.borderRadius : 0};
    background: #ffffff;
    padding: 16px;
    overflow-y: auto;
    width: 100%;
    height: 100%;
  `,
});
